import React, { useEffect, useState } from "react";
import { Button, Input, message, Row, Col, Space, Card, Typography } from "antd";
import {
    EditOutlined,
    PlusOutlined,
    DeleteOutlined,
    SearchOutlined,
    BankOutlined,
} from "@ant-design/icons";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import BankAccountDrawer from "./BankAccountDrawer";

const { Title } = Typography;

export default function BankAccounts() {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [stores, setStores] = useState([]);

    useEffect(() => {
        getStores();
        getBankAccounts();
    }, [paginationInfo.current, paginationInfo.pageSize, searchQuery]);

    const getStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
        } catch (err) {
            console.error("Error fetching stores:", err);
        }
    };

    const getBankAccounts = async () => {
        try {
            setLoading(true);
            const { current, pageSize } = paginationInfo;
            const response = await API.get(`/bank-accounts/paginate`, {
                params: {
                    page: current,
                    limit: pageSize,
                    search: searchQuery,
                },
            });
            setBankAccounts(response?.data?.bankAccounts || []);
            setPaginationInfo((prev) => ({
                ...prev,
                total: response?.data?.pagination?.totalAccounts || 0,
            }));
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch bank accounts. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (account) => {
        setAccountToDelete(account);
        setIsDeleteModalVisible(true);
    };

    const handleDeleteAccount = async () => {
        try {
            await API.delete(`/bank-accounts/${accountToDelete?._id}`);
            message.success("Bank account deleted successfully!");
            getBankAccounts();
        } catch (err) {
            console.error("Error deleting bank account:", err);
            message.error(err.response?.data?.message || "Failed to delete bank account.");
        } finally {
            setIsDeleteModalVisible(false);
        }
    };

    const handleAddAccount = async () => {
        setOpenDrawer(false);
        getBankAccounts();
    };

    const handleEditAccount = async () => {
        setOpenDrawer(false);
        getBankAccounts();
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setPaginationInfo((prev) => ({ ...prev, current: 1 }));
    };

    const getStoreName = (storeId) => {
        const store = stores.find(s => s._id === storeId);
        return store ? store.name : "N/A";
    };

    const columns = [
        {
            title: "Index",
            key: "index",
            render: (_, __, index) =>
                (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
        },
        {
            title: "Branch/Store",
            key: "storeName",
            render: (_, account) => getStoreName(account.storeId),
        },
        {
            title: "Account Name",
            dataIndex: "accountName",
            key: "accountName",
        },
        {
            title: "Bank Name",
            dataIndex: "bankName",
            key: "bankName",
        },
        {
            title: "Account Number",
            dataIndex: "accountNumber",
            key: "accountNumber",
            render: (accountNumber) => accountNumber ? `****${accountNumber.slice(-4)}` : "N/A",
        },
        {
            title: "IFSC Code",
            dataIndex: "ifscCode",
            key: "ifscCode",
        },
        {
            title: "UPI ID",
            dataIndex: "upiId",
            key: "upiId",
        },
        {
            title: "Account Type",
            dataIndex: "accountType",
            key: "accountType",
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive) => (
                <span style={{ 
                    color: isActive ? "#52c41a" : "#ff4d4f", 
                    fontWeight: "bold" 
                }}>
                    {isActive ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (record) => (
                <div>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setIsEdit(true);
                            setSelectedAccount(record);
                            setOpenDrawer(true);
                        }}
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        style={{ marginLeft: 8 }}
                        onClick={() => handleDeleteClick(record)}
                    />
                </div>
            ),
        },
    ];

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }} justify="space-between">
                <Col xs={24} sm={12} md={12}>
                    <Space style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setOpenDrawer(true);
                                setIsEdit(false);
                                setSelectedAccount(null);
                            }}
                            block
                        >
                            Add Bank Account
                        </Button>
                    </Space>
                </Col>
                <Col xs={24} sm={12} md={12}>
                    <Input
                        placeholder="Search bank accounts..."
                        prefix={<SearchOutlined />}
                        value={searchQuery}
                        onChange={handleSearch}
                        allowClear
                    />
                </Col>
            </Row>

            <Card 
                size="small" 
                style={{ 
                    marginBottom: "16px", 
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef"
                }}
            >
                <Row gutter={16} align="middle">
                    <Col>
                        <BankOutlined style={{ fontSize: "18px", color: "#1890ff", marginRight: "8px" }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0, color: "#666" }}>
                            Bank Accounts Management
                        </Title>
                    </Col>
                </Row>
            </Card>

            <div style={{ overflowX: "auto" }}>
                <CustomTable
                    columns={columns}
                    dataSource={bankAccounts}
                    onPaginationChange={(pagination) => {
                        setPaginationInfo({
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                        });
                    }}
                    loading={loading}
                    total={paginationInfo.total}
                />
            </div>

            <BankAccountDrawer
                open={openDrawer}
                setOpen={setOpenDrawer}
                data={selectedAccount}
                isEdit={isEdit}
                onSubmit={isEdit ? handleEditAccount : handleAddAccount}
                stores={stores}
            />

            <ConfirmationModal
                visible={isDeleteModalVisible}
                onConfirm={handleDeleteAccount}
                onCancel={() => setIsDeleteModalVisible(false)}
                title="Confirm Deletion"
                content="Are you sure you want to delete this bank account? This action cannot be undone."
            />
        </div>
    );
}
